import { COUNTRY_CODES, flagEmoji, isCountryCode } from '@shared/types';

/**
 * Países para el selector: nombre en español (`Intl.DisplayNames`), bandera
 * emoji y búsqueda sin tildes ni mayúsculas. Los colores del uniforme están
 * en `shared/types/countries.ts`.
 */

/** Nombres que `Intl` no trae o que la gente escribe de otra forma. */
const FALLBACK_NAMES: Record<string, string> = { XK: 'Kosovo' };
const ALIASES: Record<string, string[]> = {
  US: ['EEUU', 'EE UU', 'USA', 'Estados Unidos de America', 'Gringo', 'Gringolandia'],
  DO: ['RD', 'Dominicana', 'Quisqueya'],
  GB: ['Inglaterra', 'Escocia', 'Gales', 'UK', 'Gran Bretana'],
  NL: ['Holanda'],
  KR: ['Corea'],
  CZ: ['Republica Checa'],
  CI: ['Costa de Marfil', 'Ivory Coast'],
  MK: ['Macedonia'],
  SZ: ['Suazilandia'],
  MM: ['Birmania'],
  CV: ['Cabo Verde'],
  PR: ['Boricua'],
  AE: ['Emiratos'],
  VA: ['Vaticano'],
};

let displayNames: Intl.DisplayNames | null | undefined;
const intlName = (code: string): string | null => {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(['es'], { type: 'region' });
    } catch {
      displayNames = null;
    }
  }
  try {
    const n = displayNames?.of(code);
    return n && n !== code ? n : null;
  } catch {
    return null;
  }
};

export const countryName = (code: string): string => FALLBACK_NAMES[code] ?? intlName(code) ?? code;

/** Minúsculas sin tildes ni signos: "Perú" → "peru", "Côte d’Ivoire" → "cote d ivoire". */
export const foldText = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export interface CountryEntry {
  code: string;
  name: string;
  flag: string;
  /** Texto normalizado con nombre, código y alias, para buscar. */
  search: string;
}

let entries: CountryEntry[] | null = null;
/** Todos los países ordenados por nombre en español. */
export const allCountries = (): CountryEntry[] => {
  if (!entries) {
    entries = COUNTRY_CODES.map((code) => {
      const name = countryName(code);
      return { code, name, flag: flagEmoji(code), search: foldText([name, code, ...(ALIASES[code] ?? [])].join(' | ')) };
    }).sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }
  return entries;
};

/**
 * Busca países: primero los que empiezan por el texto (nombre o alias),
 * luego los que lo contienen; dentro de cada grupo, los populares primero
 * («republica» → República Dominicana). Ignora tildes, mayúsculas y espacios.
 */
export const searchCountries = (query: string): CountryEntry[] => {
  const q = foldText(query);
  const list = allCountries();
  if (!q) return list;
  const starts: CountryEntry[] = [];
  const contains: CountryEntry[] = [];
  for (const c of list) {
    const words = c.search.split(' | ');
    if (c.code.toLowerCase() === q || words.some((w) => w.startsWith(q))) starts.push(c);
    else if (c.search.includes(q)) contains.push(c);
  }
  const pop = (c: CountryEntry) => (POPULAR_COUNTRIES.indexOf(c.code) + 1 || 99);
  const byPopular = (a: CountryEntry, b: CountryEntry) => pop(a) - pop(b);
  return [...starts.sort(byPopular), ...contains.sort(byPopular)];
};

/** Populares en béisbol, fútbol y básquet (sección fija arriba del selector). */
export const POPULAR_COUNTRIES = ['DO', 'US', 'MX', 'VE', 'PR', 'CU', 'CO', 'BR', 'AR', 'ES', 'JP', 'KR'];

/**
 * Territorios y otros que no son miembros de la ONU (siguen siendo elegibles,
 * van en su propia sección al final). 250 − 57 = 193 miembros de la ONU.
 */
export const TERRITORIES = new Set(
  'AS AI AQ AW AX BL BM BQ BV CC CK CW CX EH FK FO GF GG GI GL GP GS GU HK HM IM IO JE KY MF MO MP MQ MS NC NF NU PF PM PN PR PS RE SH SJ SX TC TF TK TW UM VA VG VI WF XK YT'.split(' ')
);
export const isTerritory = (code: string): boolean => TERRITORIES.has(code);

// ── Recientes (últimos 5 elegidos, en este navegador) ──
const RECENT_KEY = 'sportsarena.countryRecents';
export const recentCountries = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((c): c is string => typeof c === 'string' && isCountryCode(c)).slice(0, 5) : [];
  } catch {
    return [];
  }
};
export const pushRecentCountry = (code: string): void => {
  if (!isCountryCode(code)) return;
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([code, ...recentCountries().filter((c) => c !== code)].slice(0, 5)));
  } catch {
    /* sin almacenamiento */
  }
};

// ── Sugerencia la primera vez (idioma o zona horaria del navegador) ──
const TZ_COUNTRY: Record<string, string> = {
  'America/Santo_Domingo': 'DO', 'America/Puerto_Rico': 'PR', 'America/Caracas': 'VE', 'America/Havana': 'CU',
  'America/Bogota': 'CO', 'America/Mexico_City': 'MX', 'America/Monterrey': 'MX', 'America/Tijuana': 'MX', 'America/Cancun': 'MX',
  'America/Panama': 'PA', 'America/Lima': 'PE', 'America/Santiago': 'CL', 'America/Argentina/Buenos_Aires': 'AR',
  'America/Buenos_Aires': 'AR', 'America/Sao_Paulo': 'BR', 'America/Montevideo': 'UY', 'America/Asuncion': 'PY',
  'America/La_Paz': 'BO', 'America/Guayaquil': 'EC', 'America/Guatemala': 'GT', 'America/Tegucigalpa': 'HN',
  'America/El_Salvador': 'SV', 'America/Managua': 'NI', 'America/Costa_Rica': 'CR', 'America/New_York': 'US',
  'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US', 'America/Phoenix': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'Europe/Madrid': 'ES', 'Europe/London': 'GB', 'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE', 'Europe/Rome': 'IT', 'Europe/Lisbon': 'PT', 'Europe/Amsterdam': 'NL', 'Asia/Tokyo': 'JP',
  'Asia/Seoul': 'KR', 'Asia/Taipei': 'TW', 'Asia/Shanghai': 'CN', 'Australia/Sydney': 'AU',
};
/** País probable del navegador: región del idioma (es-DO) o zona horaria. null si no se sabe. */
export const suggestedCountry = (): string | null => {
  try {
    for (const lang of navigator.languages ?? [navigator.language]) {
      const region = /^[a-z]{2,3}-([A-Z]{2})/i.exec(lang)?.[1]?.toUpperCase();
      if (region && isCountryCode(region)) return region;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (tz && TZ_COUNTRY[tz]) || null;
  } catch {
    return null;
  }
};
const SUGGEST_KEY = 'sportsarena.countrySuggest';
/** La sugerencia se muestra hasta que el jugador elige algo o la descarta. */
export const suggestionDismissed = (): boolean => {
  try {
    return localStorage.getItem(SUGGEST_KEY) === '1';
  } catch {
    return true;
  }
};
export const dismissSuggestion = (): void => {
  try {
    localStorage.setItem(SUGGEST_KEY, '1');
  } catch {
    /* sin almacenamiento */
  }
};

/** Letra para el índice A–Z (sin tildes: «Åland» → A). */
export const indexLetter = (name: string): string => foldText(name).charAt(0).toUpperCase() || '#';

export const countryLabel = (code: string | null | undefined): string =>
  code && isCountryCode(code) ? `${flagEmoji(code)} ${countryName(code)}` : '';

/** Nombre para la HUD con la bandera delante si representa a un país. */
export const nameWithFlag = (avatar: { username: string; country?: string } | undefined): string | undefined =>
  avatar ? (avatar.country && isCountryCode(avatar.country) ? `${flagEmoji(avatar.country)} ${avatar.username}` : avatar.username) : undefined;

/** Banderas de un equipo (países distintos de sus humanos, máx. 2) para el marcador. */
export const teamFlags = (players: Array<{ playerId: string; avatar: { country?: string } }>): string[] => {
  const codes: string[] = [];
  for (const p of players) {
    const c = p.avatar.country;
    if (!p.playerId.startsWith('bot_') && c && isCountryCode(c) && !codes.includes(c)) codes.push(c);
  }
  return codes.slice(0, 2);
};
